import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Calendar, Briefcase, Quote,
  GraduationCap, ArrowRight
} from "lucide-react";
import FadeIn from "../ui/FadeIn";
import PageLayout from "../ui/PageLayout";
import PageHero from "../ui/PageHero";

const programs = [
  {
    title: "Engagements",
    description: "Kindness Jams, community hangouts, nutrition drives, education outreach, arts — hands-on projects and events that bring people together through compassion.",
    icon: Calendar,
    to: "/engagements",
    color: "from-ku-gold-dark to-ku-gold",
    bgAccent: "bg-ku-gold-soft",
  },
  {
    title: "Volunteering",
    description: "Make a difference in your community. Join our volunteering programme and develop leadership, empathy, and professional skills.",
    icon: Briefcase,
    to: "/volunteering",
    color: "from-emerald-500 to-teal-400",
    bgAccent: "bg-emerald-50",
  },
  {
    title: "Testimonials",
    description: "Real stories from real people whose lives have been touched by the web of kindness — volunteers, beneficiaries, and partners.",
    icon: Quote,
    to: "/testimonials",
    color: "from-ku-purple-deep to-ku-purple",
    bgAccent: "bg-purple-50",
  },
  {
    title: "Community Engagement Program",
    description: "Structured community service programmes empowering youth to develop empathy, leadership, and social responsibility skills.",
    icon: GraduationCap,
    to: "/cep",
    color: "from-ku-gold to-ku-gold-light",
    bgAccent: "bg-amber-50",
  },
];

const Programs = () => (
  <PageLayout>
    <PageHero
      title="Our Programs"
      eyebrow="What We Do"
      subtitle="Explore the diverse programs and initiatives that form the backbone of India's kindness movement."
      light
    />

    <section className="ku-section bg-ku-cream">
      <div className="ku-container">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {programs.map((program, i) => (
            <FadeIn key={program.title} delay={i * 0.08}>
              <Link to={program.to} className="group block h-full">
                <motion.div
                  whileHover={{ y: -6, scale: 1.01 }}
                  transition={{ type: "spring", stiffness: 300 }}
                  className="ku-card h-full flex flex-col p-7 relative overflow-hidden"
                >
                  {/* Background accent */}
                  <div className={`absolute -top-10 -right-10 w-32 h-32 rounded-full ${program.bgAccent} opacity-50 blur-2xl transition-opacity duration-500 group-hover:opacity-80`} />

                  {/* Icon */}
                  <div className={`relative w-14 h-14 rounded-2xl bg-gradient-to-br ${program.color} flex items-center justify-center mb-6 shadow-card group-hover:scale-110 transition-transform duration-300`}>
                    <program.icon className="w-7 h-7 text-white" />
                  </div>

                  {/* Title */}
                  <h3 className="font-display font-bold text-ku-purple text-xl mb-3 group-hover:text-ku-purple-dark transition-colors relative">
                    {program.title}
                  </h3>

                  {/* Description */}
                  <p className="font-body text-gray-600 leading-relaxed flex-1 mb-5 relative">
                    {program.description}
                  </p>

                  {/* View button */}
                  <div className="flex items-center gap-2 text-ku-gold-dark font-accent font-bold text-sm group-hover:gap-3 transition-all duration-300 relative">
                    View
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                  </div>
                </motion.div>
              </Link>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  </PageLayout>
);

export default Programs;
